import React, { useState, useEffect, useRef, useCallback } from "react";
import { Spin } from "antd";
import {
  CloseOutlined,
  PushpinOutlined,
  PushpinFilled,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { SquarePen, Plus, MessageCircle } from "lucide-react";
import type { ChatHistory } from "@/lib/types";
import { chatTitleOf, groupChatsByDate, formatRelativeDate } from "@/utility/chatHelpers";
import Logger from "@/utility/logger";

interface ChatHistoryDrawerProps {
  visible: boolean;
  onClose: () => void;
  onNewChat: () => void;
  onOpenChat: (item: ChatHistory) => void;
  onPinChat: (item: ChatHistory) => Promise<void>;
  onDeleteChat: (item: ChatHistory) => Promise<void>;
  onRenameChat: (id: string, title: string) => Promise<void>;
  projectId: string | null;
  getChatHistory: (
    projectId: string,
    options?: any
  ) => Promise<{ items: ChatHistory[]; total: number }>;
  activeChatId: string | null;
}

const ChatHistoryDrawer: React.FC<ChatHistoryDrawerProps> = ({
  visible,
  onClose,
  onNewChat,
  onOpenChat,
  onPinChat,
  onDeleteChat,
  onRenameChat,
  projectId,
  getChatHistory,
  activeChatId,
}) => {
  const [historyTab, setHistoryTab] = useState<"recent" | "pinned">("recent");
  const [chatHistoryItems, setChatHistoryItems] = useState<ChatHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [renamingChat, setRenamingChat] = useState<ChatHistory | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const [deletingChat, setDeletingChat] = useState<ChatHistory | null>(null);
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  const loadHistory = useCallback(async (tab: "recent" | "pinned" = historyTab) => {
    if (!projectId || !getChatHistory) return;
    setIsLoading(true);
    try {
      const data = await getChatHistory(projectId, {
        page: 0,
        per_page: 50,
        conversations_only: tab === "recent",
        pinned_only: tab === "pinned",
      });
      setChatHistoryItems(data.items || []);
    } catch (err) {
      Logger.error("ChatHistoryDrawer: Failed to load chat history", err);
      setChatHistoryItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, getChatHistory, historyTab]);

  useEffect(() => {
    if (visible && projectId) {
      loadHistory(historyTab);
    }
  }, [visible, projectId, historyTab]);

  useEffect(() => {
    if (renamingChat) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renamingChat]);

  const handleTabSwitch = (tab: "recent" | "pinned") => {
    setHistoryTab(tab);
  };

  const handlePin = async (item: ChatHistory) => {
    try {
      await onPinChat(item);
      await loadHistory();
    } catch {
      Logger.error("ChatHistoryDrawer: Failed to pin/unpin chat");
    }
  };

  const handleStartRename = (item: ChatHistory) => {
    setRenamingChat(item);
    setRenameTitle(chatTitleOf(item));
  };

  const handleCancelRename = () => {
    if (isRenaming) return;
    setRenamingChat(null);
    setRenameTitle("");
  };

  const handleConfirmRename = async () => {
    if (!renamingChat || !renameTitle.trim()) return;
    if (renameTitle.trim() === chatTitleOf(renamingChat)) {
      handleCancelRename();
      return;
    }
    setIsRenaming(true);
    try {
      await onRenameChat(renamingChat.id, renameTitle.trim());
      setChatHistoryItems((prev) =>
        prev.map((entry) =>
          entry.id === renamingChat.id ? { ...entry, title: renameTitle.trim() } : entry
        )
      );
      setRenamingChat(null);
      setRenameTitle("");
    } catch {
      Logger.error("ChatHistoryDrawer: Failed to rename chat");
    } finally {
      setIsRenaming(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingChat) return;
    setIsDeletingChat(true);
    try {
      await onDeleteChat(deletingChat);
      setChatHistoryItems((prev) => prev.filter((entry) => entry.id !== deletingChat.id));
      setDeletingChat(null);
    } catch {
      Logger.error("ChatHistoryDrawer: Failed to delete chat");
    } finally {
      setIsDeletingChat(false);
    }
  };

  if (!visible) return null;

  const grouped = groupChatsByDate(chatHistoryItems);

  return (
    <div className="chat-history-drawer">
      {/* Header */}
      <div className="chat-history-drawer-header">
        <h3>Past Chats</h3>
        <button onClick={onClose} className="chat-header-btn" aria-label="Close">
          <CloseOutlined style={{ fontSize: 12 }} />
        </button>
      </div>

      {/* New Chat Button */}
      <button
        className="chat-history-new-chat-btn"
        onClick={() => {
          onNewChat();
          onClose();
        }}
      >
        <SquarePen size={14} />
        New chat
        <Plus size={14} />
      </button>

      {/* Tabs */}
      <div className="chat-history-tabs">
        <button
          className={`chat-history-tab ${historyTab === "recent" ? "active" : ""}`}
          onClick={() => handleTabSwitch("recent")}
        >
          Recent
        </button>
        <button
          className={`chat-history-tab ${historyTab === "pinned" ? "active" : ""}`}
          onClick={() => handleTabSwitch("pinned")}
        >
          Pinned
        </button>
      </div>

      {/* List */}
      <div className="chat-history-list">
        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
          </div>
        ) : chatHistoryItems.length === 0 ? (
          <div className="chat-history-empty">
            <MessageCircle size={24} strokeWidth={1.5} />
            <span>{historyTab === "pinned" ? "No pinned chats" : "No chats yet"}</span>
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.label} className="chat-history-date-group">
              <div className="chat-history-date-label">{group.label}</div>
              {group.items.map((item) => (
                <div
                  key={item.id}
                  className={`chat-history-item ${activeChatId === item.id ? "active" : ""}`}
                  onClick={() => onOpenChat(item)}
                >
                  {renamingChat?.id === item.id ? (
                    <div className="chat-history-rename" onClick={(e) => e.stopPropagation()}>
                      <input
                        ref={renameInputRef}
                        value={renameTitle}
                        onChange={(e) => setRenameTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleConfirmRename();
                          if (e.key === "Escape") handleCancelRename();
                        }}
                        className="chat-history-rename-input"
                        disabled={isRenaming}
                      />
                      <button onClick={handleConfirmRename} className="chat-history-action-btn" disabled={isRenaming}>
                        {isRenaming ? <LoadingOutlined style={{ fontSize: 10 }} spin /> : <CheckOutlined style={{ fontSize: 10 }} />}
                      </button>
                      <button onClick={handleCancelRename} className="chat-history-action-btn" disabled={isRenaming}>
                        <CloseCircleOutlined style={{ fontSize: 10 }} />
                      </button>
                    </div>
                  ) : (
                    <>
                      {item.is_pinned && (
                        <PushpinFilled style={{ fontSize: 10, color: "#f59e0b", flexShrink: 0 }} />
                      )}
                      <span className="chat-history-item-title">
                        {chatTitleOf(item)}
                      </span>
                      <span className="chat-history-item-date">
                        {formatRelativeDate(item.created_at)}
                      </span>
                      <div className="chat-history-item-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className={`chat-history-action-btn ${item.is_pinned ? "pin-active" : ""}`}
                          onClick={() => handlePin(item)}
                          title={item.is_pinned ? "Unpin" : "Pin"}
                        >
                          {item.is_pinned ? (
                            <PushpinFilled style={{ fontSize: 11 }} />
                          ) : (
                            <PushpinOutlined style={{ fontSize: 11 }} />
                          )}
                        </button>
                        <button
                          className="chat-history-action-btn"
                          onClick={() => handleStartRename(item)}
                          title="Rename"
                        >
                          <EditOutlined style={{ fontSize: 11 }} />
                        </button>
                        <button
                          className="chat-history-action-btn delete"
                          onClick={() => setDeletingChat(item)}
                          title="Delete"
                        >
                          <DeleteOutlined style={{ fontSize: 11 }} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingChat && (
        <div className="chat-delete-modal-overlay" onClick={() => !isDeletingChat && setDeletingChat(null)}>
          <div className="chat-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-delete-modal-accent" />
            <div className="chat-delete-modal-body">
              <DeleteOutlined style={{ fontSize: 24, color: "#ef4444" }} />
              <div className="chat-delete-modal-title">Delete Chat?</div>
              <div className="chat-delete-modal-text">
                This permanently removes the conversation and its answers.
                The chat <strong>"{chatTitleOf(deletingChat)}"</strong> cannot be recovered.
              </div>
            </div>
            <div className="chat-delete-modal-actions">
              <button onClick={() => setDeletingChat(null)} disabled={isDeletingChat}>
                Cancel
              </button>
              <button className="confirm" onClick={handleConfirmDelete} disabled={isDeletingChat}>
                {isDeletingChat ? <LoadingOutlined spin /> : "Delete chat"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatHistoryDrawer;
